'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { IFsNodeSchema } from '@cloudike/web_files'
import { ConfigProvider, theme } from 'antd'
import {
  assetsStatusSelector,
  selectedAssetsIdsSelector,
  selectSortedLinkAssets
} from 'features/assets/model/selectors/assetsSelectors'
import {
  assetsActions,
  copyAssetsThunk,
  downloadAssetsThunk,
  fetchBoardAllAssetsThunk,
  moveAssetsThunk
} from 'features/assets/model/slice/assetsSlice'
import { AssetsGrid } from 'features/assets/ui'
import {
  currentBoardIdSelector,
  getBoardStatus,
  getBoardDescription,
  getBoardTags,
  getSystemStatuses,
  getSystemTags,
  getSystemTagsId,
  pageStatusSelector,
  selectedBoardsIdsSelector,
  selectSortedBoards
} from 'features/boards/model/selectors/boardsSelectors'
import {
  boardsActions,
  changeBoardTagsThunk,
  copyBoardsThunk, createNewSystemTagThunk,
  downloadBoardsThunk,
  fetchBoardDataThunk,
  fetchSystemFieldsThunk,
  getAvailableToolbarActionsByUserRole,
  moveBoardsThunk
} from 'features/boards/model/slice/boardsSlice'
import { BoardToolbarAction, BoardAssetsToolbarAction } from 'shared/constants/toolbar'
import { NodeTypes } from 'shared/constants/common'
import { BoardHeader } from 'features/boards/ui/BoardHeader'
import { BoardPageToolbarTop } from 'features/boards/ui/BoardPageToolbarTop'
import { BoardsGrid } from 'features/boards/ui/BoardsGrid'
import { useBoardFields } from 'features/boards/hooks'
import { useContextMenusManager } from 'features/common/hooks/useContextMenusManager'
import { useGetToolbarExtraActions } from 'features/common/hooks/useGetToolbarExtraActions'
import { useGetToolbarMainActions } from 'features/common/hooks/useGetToolbarMainActions'
import { useOnClickOutsideCards } from 'features/common/hooks/useOnClickOutsideCards'
import { usePressEsc } from 'features/common/hooks/usePressEsc'
import { useSorting } from 'features/common/hooks/useSorting'
import { openModal } from 'features/common/model/slice/moadlSlice'
import { sharingActions } from 'features/common/model/slice/sharingSlice'
import { AdjustableSection, ItemsViewToolbar, SortingControl } from 'features/common/ui/dashboard'
import { useScrollContainer } from 'features/common/ui/dashboard/Layout'
import { MultiSelectTool } from 'features/common/ui/MultiSelectTool'
import { SkeletonBoard } from 'features/common/ui/SkeletonBoard'
import { getFilesPreviewItemsSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { uploadFilesThunk, checkFilesForRenameThunk } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { fetchFamilyThunk } from 'features/team/model/slice/teamSlice'
import { MemberRole } from 'features/team/model/types/members'
import { useGetEnablePermission } from 'features/public-link-files/hooks/useGetEnablePermission'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { ROUTES } from 'shared/constants/routesPaths'
import { parsePathFromParams } from 'shared/hooks/parsePathFromParams'
import { SDK_TYPES } from 'shared/sdk/sdkConstants'
import { AppstoreAddOutlined, LoadingOutlined, PlusCircleOutlined, Spin } from 'shared/ui'
import { BlockOutlined, CopyOutlined, DeleteOutlined, EditOutlined } from 'shared/ui/'
import { hasAccess } from 'shared/utils/access'
import styled from 'styled-components'


import { loadBoardAssetsInBackgroundThunk } from '../../features/boards/model/slice/boardsSlice'
import { BoardToolbarTop } from '../../features/boards/ui/BoardToolbarTop'
import { actions, setIsAddNewBoardModalOpen } from '../../features/common/model/slice/appSlice'
import { EmptyBoardPlaceholder } from '../../features/common/ui/EmptyBoardPlaceholder'
import { DropzoneArea } from '../../features/dropzone/DropzoneArea'
import {
  dropzoneActions,
  removeUploadHandlerThunk,
  setUploadHandlerThunk
} from '../../features/dropzone/dropzoneSlice'
import { filesPreviewActions, openFilesPreviewThunk } from '../../features/files-preview/model/slice/filesPreviewSlice'
import { clearSearchState } from '../../features/common/model/slice/searchSlice'
import { assetRootBoardIdSelector, getUserRole } from '../../features/user/model/selectors/userSelectors'
import { getDuplicateModal } from '../../features/files-uploading/selectors/filesUploadingSelectors'
import {
  ArrowRightOutlined,
  ArrowsAltOutlined,
  DownloadOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  ShareAltOutlined
} from '../../shared/ui'


type ContextItemType = 'board' | 'asset' | 'mixed' | null

interface SelectoEvent {
  selected: HTMLElement[]
  added: HTMLElement[]
  removed: HTMLElement[]
}

export const BoardPage = ({ id, isPublicLink }: { id: string, isPublicLink?: boolean }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const paths = parsePathFromParams(params, isPublicLink ? 'fs' : '')
  const currentPath = `/links/fs/${paths.join('/')}`

  // Selectors
  const selectedAssetsIds = useAppSelector(selectedAssetsIdsSelector)
  const selectedBoardsIds = useAppSelector(selectedBoardsIdsSelector)
  const boardStatus = useAppSelector(pageStatusSelector)
  const assetsStatus = useAppSelector(assetsStatusSelector)
  const userRole = useAppSelector(getUserRole)
  const isDropzoneAreaVisible = useAppSelector(state => state.dropzone.isDropzoneAreaVisible)
  const systemStatusOptions = useAppSelector(getSystemStatuses)
  const boardSystemStatus = useAppSelector(getBoardStatus)
  const systemTags = useAppSelector(getSystemTags)
  const boardTags = useAppSelector(getBoardTags)
  const boardDescription = useAppSelector(getBoardDescription)
  const tagsFieldId = useAppSelector(getSystemTagsId)
  const scrollContainerRef = useScrollContainer()
  const currentBoardId = useAppSelector(currentBoardIdSelector)
  const rootBoardId = useAppSelector(assetRootBoardIdSelector)
  const isPreviewModalOpened = useAppSelector(getFilesPreviewItemsSelector).length > 0
  const { isDownloadEnabled, isEditContentEnabled, isCreateCommentsEnabled, isFsNodeVersionsEnabled } = useGetEnablePermission()
  const allSettingsDisabled = !isDownloadEnabled && !isEditContentEnabled && !isCreateCommentsEnabled && !isFsNodeVersionsEnabled
  const duplicateModal = useAppSelector(getDuplicateModal)
  const [pendingUploadFiles, setPendingUploadFiles] = useState<FileList | File[] | null>(null)
  const { updateField: updateBoardField, deleteField: deleteBoardField } = useBoardFields(id)

  useEffect(() => {
    const queue = duplicateModal.queue
    const decisions = duplicateModal.decisions
    if(queue.length > 0 && queue.length === Object.keys(decisions).length) {
      if (pendingUploadFiles && (pendingUploadFiles as any).length > 0) {
        dispatch(uploadFilesThunk({
          files: pendingUploadFiles as any,
          symlinkParentId: id,
        }))
        setPendingUploadFiles(null)
      }
    }
  }, [duplicateModal, pendingUploadFiles])

  const availableBoardsActions = getAvailableToolbarActionsByUserRole(userRole, isDownloadEnabled, isEditContentEnabled, isPublicLink)
  const availableAssetsActions = getAvailableToolbarActionsByUserRole(userRole, isDownloadEnabled, isEditContentEnabled, isPublicLink)
  const allSortedAssets = useAppSelector(selectSortedLinkAssets)
  const allSortedBards = useAppSelector(selectSortedBoards)

  const {
    sortingOptions,
    currentSortValue,
    handleSortChange
  } = useSorting()

  const excludedMainBoardActions = [
    BoardToolbarAction.Open,
    BoardToolbarAction.RemoveFromBoard,
    // BoardToolbarAction.MakeFavorite,
    BoardToolbarAction.Rename,
    BoardToolbarAction.Copy,
    BoardToolbarAction.Delete,
    BoardToolbarAction.AddToAnotherBoard
  ]

  const excludedMainAssetsActions = [
    BoardAssetsToolbarAction.Open,
    BoardAssetsToolbarAction.RemoveFromBoard,
    // BoardAssetsToolbarAction.MakeFavorite,
    BoardAssetsToolbarAction.Rename,
    BoardAssetsToolbarAction.Copy,
    BoardAssetsToolbarAction.AddToAnotherBoard,
    BoardAssetsToolbarAction.Delete
  ]

  const boardToolbarMainActions = useGetToolbarMainActions({ availableActions: availableBoardsActions, excludedActions: excludedMainBoardActions })
  const assetsToolbarMainActions = useGetToolbarMainActions({ availableActions: availableAssetsActions, excludedActions: excludedMainAssetsActions })
  const mixedToolbarMainActions = [...boardToolbarMainActions, ...assetsToolbarMainActions]
  const actionsButtonCustomStyles = {}

  const toolbarActionsIcons = {
    [BoardToolbarAction.Rename]: <EditOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Copy]: <CopyOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Delete]: <DeleteOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Download]: <DownloadOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Share]: <ShareAltOutlined style={actionsButtonCustomStyles} />,
  }

  const contextMenuActionsIcons = {
    [BoardToolbarAction.Open]: <ArrowsAltOutlined />,
    [BoardToolbarAction.Share]: <LinkOutlined />,
    // [BoardToolbarAction.MakeFavorite]: <StarOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Delete]: <DeleteOutlined />,
    [BoardToolbarAction.Duplicate]: <BlockOutlined />,
    [BoardToolbarAction.CreateSubBoard]: <PlusCircleOutlined />,
    [BoardToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined />,
  }

  const boardExtraActions = [
    [{
      key: BoardToolbarAction.Open,
      label: 'Open',
      icon: contextMenuActionsIcons[BoardAssetsToolbarAction.Open],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && {
      key: BoardToolbarAction.Share,
      label: 'Share link',
      icon: contextMenuActionsIcons[BoardToolbarAction.Share],
    },
    // {
    //   key: BoardToolbarAction.MakeFavorite,
    //   label: 'Add to favorite',
    //   icon: contextMenuActionsIcons[BoardToolbarAction.MakeFavorite],
    // }
    ].filter(Boolean),
    [isDownloadEnabled && {
      key: BoardToolbarAction.Download,
      label: 'Download',
      icon: contextMenuActionsIcons[BoardToolbarAction.Download],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Copy],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.CreateSubBoard,
      label: 'Create sub-board',
      icon: contextMenuActionsIcons[BoardToolbarAction.CreateSubBoard],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Move,
      label: 'Move to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Move],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.AddToAnotherBoard,
      label: 'Add to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.AddToAnotherBoard],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Rename,
      label: 'Rename',
      icon: contextMenuActionsIcons[BoardToolbarAction.Rename],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Duplicate,
      label: 'Duplicate',
      icon: contextMenuActionsIcons[BoardToolbarAction.Duplicate],
    }
    ].filter(Boolean),
    [
      // {
      //   key: BoardToolbarAction.RemoveFromBoard,
      //   label: 'Remove from board',
      //   icon: contextMenuActionsIcons[BoardToolbarAction.RemoveFromBoard],
      // },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && isEditContentEnabled && {
        key: BoardToolbarAction.Delete,
        label: 'Delete from library',
        icon: contextMenuActionsIcons[BoardToolbarAction.Delete],
        danger: true,
      }
    ].filter(Boolean)
  ]

  const excludedExtraBoardActions = [
    BoardToolbarAction.Share,
    BoardToolbarAction.Download,
    BoardToolbarAction.Move,
    // BoardToolbarAction.Copy,
    BoardToolbarAction.AddToAnotherBoard,
  ]

  const assetsExtraActions = [
    [{
      key: BoardAssetsToolbarAction.Open,
      label: 'Open',
      icon: contextMenuActionsIcons[BoardAssetsToolbarAction.Open],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && {
      key: BoardToolbarAction.Share,
      label: 'Share link',
      icon: contextMenuActionsIcons[BoardToolbarAction.Share],
    },
    // {
    //   key: BoardToolbarAction.MakeFavorite,
    //   label: 'Make favorite',
    //   icon: contextMenuActionsIcons[BoardToolbarAction.MakeFavorite],
    // }
    ],
    [isDownloadEnabled && {
      key: BoardToolbarAction.Download,
      label: 'Download',
      icon: contextMenuActionsIcons[BoardToolbarAction.Download],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Copy],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.CreateSubBoard,
      label: 'Create sub-board',
      icon: contextMenuActionsIcons[BoardToolbarAction.CreateSubBoard],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Move,
      label: 'Move to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Move],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.AddToAnotherBoard,
      label: 'Add to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.AddToAnotherBoard],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Rename,
      label: 'Rename',
      icon: contextMenuActionsIcons[BoardToolbarAction.Rename],
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      key: BoardToolbarAction.Duplicate,
      label: 'Duplicate',
      icon: contextMenuActionsIcons[BoardToolbarAction.Duplicate],
    }
    ].filter(Boolean),
    [
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && isEditContentEnabled && {
        key: BoardToolbarAction.RemoveFromBoard,
        label: 'Remove from board',
        icon: contextMenuActionsIcons[BoardToolbarAction.RemoveFromBoard],
      },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && isEditContentEnabled && {
        key: BoardToolbarAction.Delete,
        label: 'Delete from library',
        icon: contextMenuActionsIcons[BoardToolbarAction.Delete],
        danger: true,
      }
    ].filter(Boolean)
  ]
  const excludedExtraAssetsActions = [
    BoardToolbarAction.Share,
    BoardToolbarAction.Download,
    BoardToolbarAction.Move,
    BoardToolbarAction.Copy,
    BoardToolbarAction.CreateSubBoard,
    BoardToolbarAction.Duplicate,
    // BoardToolbarAction.MakeFavorite
  ]
  // filter excluded extra board actions from boardExtraActions
  const boardToolbarExtraActions = useGetToolbarExtraActions({ availableActions:boardExtraActions, excludedActions:excludedExtraBoardActions })
  const assetsToolbarExtraActions = useGetToolbarExtraActions({ availableActions:assetsExtraActions, excludedActions:excludedExtraAssetsActions })
  // Current state
  const isAssetInTrash = (item) => item.linked_node_is_trashed || item.is_trashed || item.is_explicitly_trashed
  const isInBoard = (item) => item.parent_id === id
  const isBoardInTrash = (item) => item.is_explicitly_trashed || item.is_trashed
  const isCurrentBoard = (item) => item.id === id
  const isAssetVisible = (item) => isInBoard(item) && !isAssetInTrash(item)
  const isBoardVisible = (item) => isInBoard(item) && !isBoardInTrash(item)
  const board = allSortedBards.find(isCurrentBoard)
  const subBoards = allSortedBards.filter(isBoardVisible)
  const assets = allSortedAssets.filter(isAssetVisible)
  const assetsRef = useRef(assets)
  const selectedBoards = allSortedBards.filter(board => selectedBoardsIds.includes(board.id))
  const selectedAssets = assets.filter(asset => selectedAssetsIds.includes(asset.id))
  const inputRef = useRef<HTMLInputElement>(null)

  // UI state
  const [withPressedCtrl, setWithPressedCtrl] = useState(false)
  const [withPressedShift, setWithPressedShift] = useState(false)
  const [isMouseSelecting, setIsMouseSelecting] = useState<boolean>(false)

  // useEffect to update assetsRef when assets change
  useEffect(() => {
    assetsRef.current = assets
  }, [assets])


  // Замена отдельных состояний контекстных меню на единый хук
  const {
    handleContextMenuStateChange,
    isContextMenuOpen
  } = useContextMenusManager()

  const isSubBoardsEmpty = subBoards.length === 0
  const isAssetsEmpty = assets.length === 0
  const isEmptyBoard = isSubBoardsEmpty && isAssetsEmpty
  const isBoardsLoading = boardStatus === LOADING_STATUSES.LOADING
  const isAssetsLoading = assetsStatus === LOADING_STATUSES.LOADING
  const isPageLoading = isBoardsLoading || isAssetsLoading
  const selectedAssetsTargetClassName = 'js-asset-card'
  const selectedBoardsTargetClassName = 'js-board-card'
  const abortControllerRef = useRef(null)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current
    dispatch(fetchBoardDataThunk({ nodeId: id, signal }))
    dispatch(fetchSystemFieldsThunk({ signal }))

    return () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    }
  }, [id])

  // Effects
  useEffect(() => {
    if (!id) return
    dispatch(boardsActions.setCurrentBoardId(id))
    dispatch(fetchBoardAllAssetsThunk({ boardId: id }))
    dispatch(clearSearchState())

    return () => {
      dispatch(assetsActions.resetState())
      dispatch(boardsActions.setCurrentBoardId(null))
      dispatch(boardsActions.setCurrentBoard(null))
      unselectAll()
    }
  }, [id, dispatch])

  useEffect(() => {
    if (board) {
      dispatch(boardsActions.setCurrentBoard(board))
    }
  }, [board, dispatch])

  useEffect(() => {
    dispatch(fetchFamilyThunk())
  }, [])

  useEffect(() => {
    if (!isPreviewModalOpened) return

    const urlSearchParams = new URLSearchParams(window.location.search)
    const fileId = urlSearchParams.get('prv')

    if (fileId && id) {
      dispatch(loadBoardAssetsInBackgroundThunk({
        boardId: id,
        currentFileId: fileId,
        callback: (items, currentIndex) => {
          dispatch(filesPreviewActions.updatePreviewWithBackgroundData({
            items,
            currentIndex,
            totalItemsCount: items.length
          }))
        }
      }))
    }
  }, [id, isPreviewModalOpened])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(true)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(true)
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(false)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  // dropzone settings
  useEffect(() => {
    dispatch(setUploadHandlerThunk({ callback: handleUploadFiles }))
    dispatch(dropzoneActions.updateDropzoneOptions({
      disabled: userRole === MemberRole.GUEST ? true : false
    }))
    dispatch(dropzoneActions.toggleDropzoneAreaVisibility(true))

    return () => {
      dispatch(removeUploadHandlerThunk())
      dispatch(dropzoneActions.updateDropzoneOptions({
        disabled: true
      }))
    }
  }, [userRole])

  // Handlers
  const handleClickOutsideBoards = () => {
    // if(isMouseSelecting) return
    // dispatch(boardsActions.unselectAll())
  }

  const handleClickOutsideAssets = () => {
    // if(isMouseSelecting) return
    // dispatch(assetsActions.unselectAll())
  }

  const unselectAll = useCallback(() => {
    dispatch(boardsActions.unselectAll())
    dispatch(assetsActions.unselectAll())
  }, [dispatch])

  usePressEsc(unselectAll)
  useOnClickOutsideCards(unselectAll)

  const handleSelect = (item: IFsNodeSchema, isAsset: boolean) => {
    const actions = isAsset ? assetsActions : boardsActions

    if (withPressedCtrl) {
      dispatch(actions.selectItemWithPressedCtrl({ id: item.id }))
    } else if (withPressedShift) {
      dispatch(actions.selectItemWithPressedShift({ id: item.id, items: isAsset ? assets : subBoards }))
    } else {
      dispatch(actions.selectItem({ id: item.id }))
    }
  }

  const handleSelectAsset = (asset: IFsNodeSchema) => {
    handleSelect(asset, true)
  }

  const handleSelectBoard = (board: IFsNodeSchema) => {
    handleSelect(board, false)
  }

  const openBoard = (board: IFsNodeSchema) => {
    if (isPublicLink) {
      navigate(`${currentPath}/${board.id}`)
    } else {
      navigate(`${ROUTES.dashboard.board}/${board.id}`)
    }
  }

  const handleBoardDoubleClick = (board: IFsNodeSchema) => {
    openBoard(board)
  }

  // Generic context menu action handler
  const handleContextActionClick = async (
    itemType: ContextItemType,
    action: BoardToolbarAction,
    itemId: string,
    items: IFsNodeSchema[]
  ) => {
    const targetItem = items.find(item => item.id === itemId)
    if (!targetItem) return
    switch (action) {
    case BoardToolbarAction.Open:
      if (itemType === 'board') {
        navigate(`${ROUTES.dashboard.board}/${itemId}`)
      }
      if (itemType === 'asset') {
        dispatch(openFilesPreviewThunk({
          items: assets as any,
          currentItemId: itemId,
          totalItemsCount: 1,
          navigate
        }))
        dispatch(assetsActions.unselectAll())
      }
      break
    case BoardToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [itemId], items: [targetItem] } }))
      break
    case BoardToolbarAction.Copy:
    case BoardToolbarAction.Move:
    case BoardToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: action, context: itemType, data: { ids: [itemId], items: [targetItem] } }))
      break
    case BoardToolbarAction.Duplicate:
      await dispatch(copyBoardsThunk({ ids: [itemId], destinationId: id })).unwrap()
      dispatch(boardsActions.unselectAll())
      break
    case BoardToolbarAction.Delete:
      dispatch(openModal({
        type: BoardToolbarAction.Delete,
        context: itemType, data: {
          ids: [itemId],
          items: [targetItem],
          redirectTo: itemId === id ? board?.parent_id === rootBoardId ? ROUTES.dashboard.allAssets : `${ROUTES.dashboard.board}/${board?.parent_id}` : null
        }
      }))
      break
    case BoardToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: [itemId], items: [targetItem] } }))
      break
    case BoardToolbarAction.CreateSubBoard:
      if(itemType === 'board') {
        dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: itemId }))
        dispatch(setIsAddNewBoardModalOpen(true))
      }
      break
    case BoardToolbarAction.Download:
      if (itemType === 'board') {
        dispatch(downloadBoardsThunk({ ids: [itemId] }))
      } else if (itemType === 'asset') {
        dispatch(downloadAssetsThunk({ ids: [itemId] }))
      }
      unselectAll()
      break
    case BoardToolbarAction.Share:
      dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [itemId], items: [targetItem] } }))
      dispatch(sharingActions.setSharingAlbumConfig({
        sdkType: SDK_TYPES.FILES
      }))
      dispatch(sharingActions.setSharedItemData(targetItem))
      break
    }
  }

  // Board context menu action handlers
  const handleBoardContextActionClick = (action: BoardToolbarAction, itemId: string) => {
    handleContextActionClick('board', action, itemId, allSortedBards)
  }

  // Asset context menu action handlers
  const handleAssetsContextActionClick = (action: BoardAssetsToolbarAction, itemId: string) => {
    const currentAssets = assetsRef.current
    handleContextActionClick('asset', action as unknown as BoardToolbarAction, itemId, currentAssets)
  }

  const handleAssetDropOnBoard = useCallback((assetIds: string[], boardId: string) => {
    if (assetIds.length > 0 && boardId) {
      dispatch(moveAssetsThunk({ ids: assetIds, destinationId: boardId }))
      dispatch(assetsActions.unselectAll())
    }
  }, [dispatch])

  const handleBoardDropOnBoard = useCallback((boardIds: string[], targetBoardId: string) => {
    if (boardIds.length > 0 && targetBoardId) {
      dispatch(moveBoardsThunk({ ids: boardIds, destinationId: targetBoardId }))
      dispatch(boardsActions.unselectAll())
    }
  }, [dispatch])

  const handleBoardDragStart = () => undefined

  // Generic toolbar action handler
  const handleToolbarActionClick = async (
    itemType: ContextItemType,
    actionId: string,
    items: any[]
  ) => {
    const assetsIds = items.filter(item => item.type === 'link').map(item => item.id)
    const boardsIds = items.filter(item => item.type === 'dir').map(item => item.id)

    switch (actionId) {
    case BoardToolbarAction.Open:
      if (items.length === 1) {
        const item = items[0]
        if(item.type === 'dir') {
          navigate(`${ROUTES.dashboard.board}/${item.id}`)
        }
        if(item.type === 'link') {
          dispatch(openFilesPreviewThunk({
            items: assets as any,
            currentItemId: item.id,
            totalItemsCount: 1,
            currentBoardInfo: board,
            navigate
          }))
          dispatch(assetsActions.unselectAll())
        }
      }
      break
    case BoardToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [...boardsIds, ...assetsIds], items: [...selectedBoards, ...selectedAssets] } }))
      break
    case BoardToolbarAction.Copy:
      dispatch(openModal({ type: BoardToolbarAction.Copy, context: itemType, data: { ids: [...boardsIds, ...assetsIds], items: [...selectedBoards, ...selectedAssets] } }))
      break
    case BoardToolbarAction.Move:
      dispatch(openModal({ type: BoardToolbarAction.Move, context: itemType, data: { ids: [...boardsIds, ...assetsIds], items: [...selectedBoards, ...selectedAssets] } }))
      break
    case BoardToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: BoardToolbarAction.AddToAnotherBoard, context: itemType, data: { ids: [...boardsIds, ...assetsIds], items: [...selectedBoards, ...selectedAssets] } }))
      break
    case BoardToolbarAction.Delete:
      dispatch(openModal({ type: BoardToolbarAction.Delete, context: itemType, data: { ids: [...selectedBoardsIds, ...selectedAssetsIds], items: [...selectedBoards, ...selectedAssets] } }))
      break
    case BoardToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: [ ...selectedAssetsIds], items: [...selectedAssets] } }))
      break
    case BoardToolbarAction.Download:
      if (itemType === 'board') {
        dispatch(downloadBoardsThunk({ ids: boardsIds }))
      } else if (itemType === 'asset') {
        dispatch(downloadAssetsThunk({ ids: assetsIds }))
      } else if (itemType === 'mixed') {
        dispatch(downloadBoardsThunk({ ids: [...boardsIds, ...assetsIds] }))
      }
      unselectAll()
      break
    case BoardToolbarAction.Duplicate:
      if (itemType === 'board') {
        await dispatch(copyBoardsThunk({ ids: boardsIds, destinationId: id })).unwrap()
        dispatch(boardsActions.unselectAll())
      } else if(itemType === 'asset') {
        await dispatch(copyAssetsThunk({ ids: assetsIds, destinationId: id })).unwrap()
        dispatch(assetsActions.unselectAll())
      }
      break
    case BoardToolbarAction.CreateSubBoard:
      if(itemType === 'board') {
        dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: id }))
        dispatch(setIsAddNewBoardModalOpen(true))
      }
      break
    case BoardToolbarAction.Share:
      dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: itemType ? [ ...boardsIds] :  [...assetsIds], items: itemType ? [...selectedBoards] : [...selectedAssets] } }))
      dispatch(sharingActions.setSharingAlbumConfig({
        sdkType: SDK_TYPES.FILES
      }))
      if (itemType === 'board') {
        dispatch(sharingActions.setSharedItemData(selectedBoards[0]))
      } else if (itemType === 'asset') {
        dispatch(sharingActions.setSharedItemData(selectedAssets[0]))
      }
      break
    }
  }


  // Loading and error states
  if (!board && !isPageLoading) {
    return (
      <div>
        Board not found
      </div>
    )
  }

  if (isPageLoading) {
    return <SkeletonBoard />
  }


  const handleCloseToolbar = () => {
    dispatch(assetsActions.unselectAll())
    dispatch(boardsActions.unselectAll())
  }

  function handleUploadFiles (files: FileList, rejectedFiles: File[], boardId?: string) {
    if (!files?.length) return
    setPendingUploadFiles(files)
    dispatch(checkFilesForRenameThunk({
      files: files as any,
      parentId: boardId || id,
      type: NodeTypes.FILE,
    })).unwrap().then((duplicates) => {
      if (!duplicates) return
      if (duplicates.flat().length > 0) return

      dispatch(uploadFilesThunk({
        files: files as any,
        symlinkParentId: boardId || id,
        modificators: [],
      }))

      inputRef.current.value = ''
      setPendingUploadFiles(null)
    })
  }

  const handleMouseSelect = (_: SelectoEvent, result: { id: string, targetClassName: string }[]) => {
    const selectedItems = result.map((item) => {
      if(item.targetClassName === selectedBoardsTargetClassName) {
        const board = subBoards.find((board) => board.id === item.id)
        return {
          ...board,
          itemType: 'board',
        }
      } else if(item.targetClassName === selectedAssetsTargetClassName) {
        const asset = assets.find((asset) => asset.id === item.id)
        return {
          ...asset,
          itemType: 'asset',
        }
      }
    })
    const selectedAssets = selectedItems.filter((item) => item.itemType === 'asset')
    const selectedBoards = selectedItems.filter((item) => item.itemType === 'board')
    if (selectedAssets.length || selectedBoards.length) {
      const selectedAssetsIds = selectedAssets.map((asset) => asset.id)
      const selectedBoardsIds = selectedBoards.map((board) => board.id)
      dispatch(assetsActions.setSelectedItemsIds(selectedAssetsIds))
      dispatch(boardsActions.setSelectedItemsIds(selectedBoardsIds))
    }
  }

  const handleMouseSelectEnd = () => {
    setIsMouseSelecting(false)
  }

  const handleMouseSelectStart = () => {
    setIsMouseSelecting(true)
  }

  const handleBoardToolbarTopAction = (actionId: BoardToolbarAction) => {
    handleBoardContextActionClick(actionId, id)
  }

  const handleAddBoard = () => {
    dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: id }))
    dispatch(setIsAddNewBoardModalOpen(true))
  }

  const handleBoardStatusChange = (status: any) => {
    const statusId = status?.id
    if (statusId) {
      updateBoardField('status', statusId).catch(() => null)
    } else {
      deleteBoardField('status').catch(() => null)
    }
  }

  const handleChangeBoardDescription = (value: string) => {
    updateBoardField('description', value || '').catch(() => null)
  }

  const STATUS_MENU_ITEMS = [
    {
      label: 'In progress',
      key: 'In progress',
      color: 'volcano',
    },
    {
      label: 'Waiting for approval',
      key: 'Waiting for approval',
      color: 'blue',
    },
    {
      label: 'Ready to use',
      key: 'Ready to use',
      color: 'green',
    },
    {
      label: 'Remove status',
      key: 'remove-status',
      color: 'red',
    }
  ]

  let  boardUIStatus = null
  let boardUIStatuses = systemStatusOptions.map((status) => {
    const statusValue = status.value
    const uiStatus = STATUS_MENU_ITEMS.find((item) => item.key === statusValue)
    return {
      ...status,
      ...uiStatus,
    }
  })
  boardUIStatuses.push({
    id: null,
    label: 'Remove status',
    key: 'remove-status',
    color: 'red',
  })
  if(boardSystemStatus) {
    boardUIStatus = boardUIStatuses.find((item) => item.id === boardSystemStatus)
  }
  if(!boardUIStatus) {
    boardUIStatuses = boardUIStatuses.filter((item) => item.label !== 'Remove status')
  } else {
    boardUIStatuses = boardUIStatuses.filter((item) => item.label !== boardUIStatus.label)
  }

  const handleChangeBoardTags = (newTags: any) => {
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current.signal
    dispatch(changeBoardTagsThunk({ nodeId: id, tagsFieldID: tagsFieldId, data: { value: [...newTags] }, signal }))
      .then(() => {
        abortControllerRef.current = null
      })
      .catch(() => {
        abortControllerRef.current = null
      })
  }

  const handleCreateTag = (value: string) => {
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current.signal
    dispatch(createNewSystemTagThunk({ fieldID: tagsFieldId, systemTags, boardTags, nodeId: id, newTagValue:value, signal }))
      .then(() => {
        abortControllerRef.current = null
      })
      .catch(() => {
        abortControllerRef.current = null
      })
  }

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm, token: { colorPrimary: 'var(--color-primary)' }, components: {
      Button: {
        colorPrimary: 'var(--color-primary)',
        algorithm: false,
      },
      Select: {
        algorithm: false,
        colorBgElevated: 'var(--color-white)',
        multipleItemBorderColor: 'transparent',
        multipleItemBg: 'rgba(0, 0, 0, 0.08)',
      },

    } }}
    >
      <SBoardPage $needHeight={isEmptyBoard && !isPageLoading}>
        <BoardToolbarTop
          status={boardUIStatus}
          onClickAction={handleBoardToolbarTopAction}
          onChangeStatus={handleBoardStatusChange}
          statuses={boardUIStatuses}
          disabled={!hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER])}
          onAddBoard={handleAddBoard}
          withOnlyStatus={allSettingsDisabled}
        />

        <BoardHeader
          title={board?.name}
          boardTags={boardTags}
          systemTags={systemTags}
          onChangeTags={handleChangeBoardTags}
          onCreateTag={handleCreateTag}
          description={boardDescription}
          onChangeDescription={handleChangeBoardDescription}
          disabled={!hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER])}
        />

        <ItemsViewToolbar>
          <SortingControl
            options={sortingOptions}
            value={currentSortValue}
            onChange={handleSortChange}
            placeholder="Sort by"
          />
        </ItemsViewToolbar>

        {!isSubBoardsEmpty && (
          <SBoardsSection title="Boards">
            {isBoardsLoading && (
              <Spin indicator={(
                <LoadingOutlined style={{ fontSize: 48 }}
                  spin
                />
              )}
              />
            )}

            {!isBoardsLoading && !isSubBoardsEmpty && (
              <BoardsGrid
                boards={subBoards}
                selectedItemsIds={selectedBoardsIds}
                selectedAssetsIds={selectedAssetsIds}
                assets={assets}
                onClickOutside={handleClickOutsideBoards}
                onSelect={handleSelectBoard}
                onBoardDoubleClick={handleBoardDoubleClick}
                onMenuItemClick={handleBoardContextActionClick}
                selectedTargetClassName={selectedBoardsTargetClassName}
                isItemHoverEnabled={!isMouseSelecting}
                onContextStateChanged={(isOpened) => handleContextMenuStateChange('boards', isOpened)}
                isOuterContextMenuOpened={isContextMenuOpen('assets')}
                onAssetDropOnBoard={handleAssetDropOnBoard}
                onBoardDropOnBoard={handleBoardDropOnBoard}
                onBoardDragStart={handleBoardDragStart}
              />
            )}
          </SBoardsSection>
        )}

        {!isAssetsEmpty && !isAssetsLoading && (
          <SAssetsSection title="Assets">
            {isAssetsLoading && (
              <Spin indicator={(
                <LoadingOutlined style={{ fontSize: 48 }}
                  spin
                />
              )}
              />
            )}

            {!isAssetsLoading && (
              <AssetsGrid
                assets={assets}
                board={board}
                onSelect={handleSelectAsset}
                onClickOutside={handleClickOutsideAssets}
                selectedItemsIds={selectedAssetsIds}
                selectedBoardsIds={selectedBoardsIds}
                boards={subBoards}
                onMenuItemClick={handleAssetsContextActionClick}
                selectedTargetClassName={selectedAssetsTargetClassName}
                isItemHoverEnabled={!isMouseSelecting}
                onContextStateChanged={(isOpened) => handleContextMenuStateChange('assets', isOpened)}
                isOuterContextMenuOpened={isContextMenuOpen('boards')}
                onDoubleClick={unselectAll}
              />
            )}
          </SAssetsSection>
        )}


        {isEmptyBoard && !isPageLoading && (
          <EmptyBoardPlaceholder texts={{
            title:'Upload content to this board',
            description: 'Drag and drop here to upload from your computer, or click the button below',
            uploadBtn: 'Upload content'
          }}
          onUploadFiles={handleUploadFiles}
          inputRef={inputRef}
          userRole={userRole}
          />
        )}


        <BoardPageToolbarTop
          selectedItems={[...selectedBoards, ...selectedAssets]}
          boardToolbarMainActions={boardToolbarMainActions}
          assetsToolbarMainActions={assetsToolbarMainActions}
          mixedToolbarMainActions={boardToolbarMainActions}
          boardToolbarExtraActions={boardToolbarExtraActions}
          assetsToolbarExtraActions={assetsToolbarExtraActions}
          mixedToolbarExtraActions={null}
          toolbarActionsIcons={toolbarActionsIcons}
          onToolbarActionClick={handleToolbarActionClick}
          onCloseToolbar={handleCloseToolbar}
        />

        <StyledDropzoneArea
          visible={isDropzoneAreaVisible}
          text={'The files will be uploaded to the board'}
        />


        <MultiSelectTool
          onSelect={handleMouseSelect}
          onSelectStart={handleMouseSelectStart}
          onSelectEnd={handleMouseSelectEnd}
          selectedTargetClassNames={[selectedBoardsTargetClassName, selectedAssetsTargetClassName]}
          scrollRef={scrollContainerRef}
        />
      </SBoardPage>
    </ConfigProvider>
  )
}

export const WrappedBoardPage = () => {
  const { id } = useParams<{ id: string }>()

  return <BoardPage id={id} />
}

const SBoardPage = styled.div<{ $needHeight: boolean }>`
  position: relative;
  height: ${({ $needHeight }) => $needHeight ? '100%' : 'unset'};
`

const SBoardsSection = styled(AdjustableSection)`
  margin-top: 40px;
`

const SAssetsSection = styled(AdjustableSection)`
  margin-top: 48px;
`

const StyledDropzoneArea = styled(DropzoneArea)`
  position: fixed;
  top: 70px;
  left: 270px;
  width: calc(100vw - 283px);
  height: calc(100vh - 75px);
  z-index: 1000;
  pointer-events: none;
`
